import { Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { BiSolidError } from "react-icons/bi";
import { Input } from "../../components/Input/input";
import {
  validatePassword,
  validateRepeatPassword,
} from "../../utils/validators";
import Button, { ButtonColors, ButtonTypes } from "../../components/Button";
import { RecoveryAccountHeader } from "../../components/RecoveryAccountHeader/RecoveryAccountHeader";
import { UserService } from "../../services/UsersService";
import {
  OIDC_DEFAULT_TENANT,
  OIDC_DEFAULT_USER_POOL_ID,
} from "../../shared/Config";
import {
  RECOVERY_PASSWORD_JWT_STORAGE_KEY,
  hydrateRecoveryJwtFromUrlOrStorage,
  clearRecoveryJwtStorage,
  stripRecoveryJwtFromUrlHash,
} from "../../utils/recoveryFlowStorage";

type FormValues = {
  password: string;
  repeatPassword: string;
};

function getErrorMessage(err: unknown): string {
  const ax = err as {
    response?: { data?: { message?: string | string[] } };
  };
  const msg = ax.response?.data?.message;
  if (Array.isArray(msg)) {
    const parts = msg.filter((m) => m != null && String(m).trim() !== "");
    if (parts.length > 0) return parts.join(" ");
  }
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return "Não foi possível atualizar a senha. Confira os requisitos na tela e tente novamente.";
}

export function ChangePassword() {
  const [recoveryToken] = useState(() =>
    hydrateRecoveryJwtFromUrlOrStorage(RECOVERY_PASSWORD_JWT_STORAGE_KEY),
  );

  useLayoutEffect(() => {
    stripRecoveryJwtFromUrlHash();
  }, []);

  const [phase, setPhase] = useState<"loading" | "invalid" | "ready">(
    "loading",
  );
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: { password: "", repeatPassword: "" },
  });
  const { handleSubmit } = form;

  const redirectToLogin = useCallback(() => {
    const base =
      OIDC_DEFAULT_USER_POOL_ID && OIDC_DEFAULT_TENANT
        ? `/${OIDC_DEFAULT_USER_POOL_ID}/${OIDC_DEFAULT_TENANT}/`
        : "/";
    setTimeout(() => {
      window.location.href = base;
    }, 2000);
  }, []);

  useEffect(() => {
    if (!recoveryToken) {
      setPhase("invalid");
      return;
    }

    let cancelled = false;
    UserService.validateRecoveryPasswordToken(recoveryToken)
      .then(() => {
        if (!cancelled) setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) {
          clearRecoveryJwtStorage(RECOVERY_PASSWORD_JWT_STORAGE_KEY);
          setPhase("invalid");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [recoveryToken]);

  function onSubmit(data: FormValues) {
    setSubmitting(true);
    UserService.completeRecoveryPassword(recoveryToken, data.password)
      .then(() => {
        clearRecoveryJwtStorage(RECOVERY_PASSWORD_JWT_STORAGE_KEY);
        toast.success("Senha atualizada com sucesso.");
        form.reset({ password: "", repeatPassword: "" });
        redirectToLogin();
      })
      .catch((err) => {
        const msg = getErrorMessage(err);
        toast.error(msg);
      })
      .finally(() => setSubmitting(false));
  }

  if (phase === "loading") {
    return (
      <Container fluid className="px-3">
        <RecoveryAccountHeader title="Redefinir senha" />
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "60vh" }}
        >
          <Spinner animation="border" style={{ color: "#005ca8" }} />
          <p className="mt-3 mb-0 text-muted">Validando link…</p>
        </div>
      </Container>
    );
  }

  if (phase === "invalid") {
    return (
      <Container fluid className="px-3">
        <RecoveryAccountHeader title="Redefinir senha" />
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "60vh" }}
        >
          <Card style={{ padding: 24, maxWidth: 480 }}>
            <Card.Title style={{ textAlign: "center" }}>
              <span className="d-inline-flex align-items-center gap-2 justify-content-center">
                <BiSolidError style={{ fontSize: 22, color: "#EF4F4F" }} />
                Link inválido, expirado ou já utilizado.
              </span>
            </Card.Title>
            <Card.Body className="text-center text-muted small">
              Solicite um novo link de recuperação ou entre em contato com o
              suporte.
            </Card.Body>
          </Card>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3 pb-5">
      <RecoveryAccountHeader title="Redefinir senha" />
      <Row className="justify-content-center">
        <Col xs={12} md={8} lg={5}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Card.Title className="mb-3">Nova senha</Card.Title>
              <p className="text-muted small mb-4">
                Defina uma nova senha para sua conta. O link de recuperação
                só pode ser usado uma vez.
              </p>
              <ul className="text-muted small mb-4 ps-3">
                <li>Entre 8 e 16 caracteres</li>
                <li>Pelo menos uma letra maiúscula e uma minúscula</li>
                <li>Pelo menos um número</li>
                <li>Pelo menos um símbolo: {'# ! @ $ % ^ & *'}</li>
              </ul>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="gy-3">
                  <Col xs={12}>
                    <Input
                      inputName="password"
                      label="Nova senha"
                      form={form}
                      type="password"
                      showPasswordToggle
                      validators={{ validate: validatePassword }}
                    />
                  </Col>
                  <Col xs={12}>
                    <Input
                      inputName="repeatPassword"
                      label="Confirmar senha"
                      form={form}
                      type="password"
                      showPasswordToggle
                      validators={{
                        validate: validateRepeatPassword(form),
                      }}
                    />
                  </Col>
                  <Col xs={12} className="pt-2 d-flex justify-content-end">
                    <Button
                      text={
                        submitting ? "Alterando senha…" : "Redefinir senha"
                      }
                      color={ButtonColors.primary}
                      type={ButtonTypes.submit}
                      disabled={submitting}
                    />
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
